import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  DollarSign, TrendingUp, TrendingDown, Receipt, ChevronLeft, ChevronRight, ShoppingCart, Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const AdminGananciasPage = () => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/finance/profit-summary?month=${month}&year=${year}`);
        setData(res.data);
      } catch (error) { console.error('Error fetching finance data:', error); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [month, year]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const fmt = (n) => `$${(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-ganancias-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Ganancias</h1>
        <p className="text-gray-500 mt-1">Resumen financiero de ventas y rentabilidad</p>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-center gap-4" data-testid="month-nav">
        <Button variant="outline" size="icon" onClick={prevMonth} className="rounded-full h-9 w-9" data-testid="prev-month">
          <ChevronLeft size={18} />
        </Button>
        <div className="text-center min-w-[180px]">
          <p className="text-xl font-bold text-gray-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {MONTHS[month - 1]} {year}
          </p>
        </div>
        <Button variant="outline" size="icon" onClick={nextMonth} className="rounded-full h-9 w-9" data-testid="next-month">
          <ChevronRight size={18} />
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="finance-kpis">
        {/* Total Sales */}
        <Card className="border-0 shadow-md bg-gradient-to-br from-[#467322] to-[#3A601C] text-white overflow-hidden relative">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/70 text-sm font-medium">Ventas Totales del Mes</p>
                <p className="text-3xl font-bold mt-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="total-sales">
                  {fmt(data?.total_sales)}
                </p>
                <p className="text-white/60 text-xs mt-1">{data?.total_orders || 0} órdenes</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
                <DollarSign size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Costs */}
        <Card className="border border-red-100 shadow-md overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Costos Totales del Mes</p>
                <p className="text-3xl font-bold mt-2 text-red-600" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="total-costs">
                  {fmt(data?.total_costs)}
                </p>
                <p className="text-gray-400 text-xs mt-1">Costo de productos vendidos</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center">
                <TrendingDown size={24} className="text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Net Profit */}
        <Card className="border border-emerald-100 shadow-md overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Ganancia Neta</p>
                <p className={`text-3xl font-bold mt-2 ${(data?.net_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="net-profit">
                  {fmt(data?.net_profit)}
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  Margen: <span className={`font-semibold ${(data?.margin_percentage || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {data?.margin_percentage || 0}%
                  </span>
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                <TrendingUp size={24} className="text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sample Costs Banner */}
      {(data?.sample_costs || 0) > 0 && (
        <Card className="border border-purple-100 bg-purple-50/50">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <Receipt size={16} className="text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-purple-800">Gasto en Muestras (Publicidad)</p>
                <p className="text-xs text-purple-500">Costo de productos entregados como muestras este mes</p>
              </div>
            </div>
            <p className="text-lg font-bold text-purple-700" data-testid="sample-costs">{fmt(data?.sample_costs)}</p>
          </CardContent>
        </Card>
      )}

      {/* Orders Breakdown */}
      <Card className="border border-gray-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-gray-800 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <ShoppingCart size={18} className="text-[#467322]" />
            Detalle de Órdenes — {MONTHS[month - 1]} {year}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead># Orden</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead className="text-right">Venta</TableHead>
                <TableHead className="text-right">Costo</TableHead>
                <TableHead className="text-right">Ganancia</TableHead>
                <TableHead className="text-center">Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!data?.orders?.length ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-gray-400">
                    <DollarSign size={36} className="mx-auto mb-2 opacity-40" />
                    <p>No hay órdenes registradas en este mes</p>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {data.orders.map((o, i) => (
                    <TableRow key={o.order_number} className="hover:bg-gray-50" data-testid={`finance-order-${o.order_number}`}>
                      <TableCell><Badge variant="outline" className="font-mono text-xs">{o.order_number}</Badge></TableCell>
                      <TableCell className="font-medium text-gray-800">{o.client_name}</TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {new Date(o.date).toLocaleDateString('es-DO', { day: '2-digit', month: 'short' })}
                      </TableCell>
                      <TableCell className="text-right font-medium">{fmt(o.sales)}</TableCell>
                      <TableCell className="text-right text-red-500">{fmt(o.costs)}</TableCell>
                      <TableCell className={`text-right font-bold ${o.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {fmt(o.profit)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-xs ${o.status === 'entregada' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {o.status === 'entregada' ? 'Entregada' : 'En Proceso'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Totals Row */}
                  <TableRow className="bg-gray-50 font-bold border-t-2">
                    <TableCell colSpan={3} className="text-right text-gray-600">TOTALES</TableCell>
                    <TableCell className="text-right text-gray-800">{fmt(data.total_sales)}</TableCell>
                    <TableCell className="text-right text-red-600">{fmt(data.total_costs)}</TableCell>
                    <TableCell className={`text-right ${data.net_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(data.net_profit)}</TableCell>
                    <TableCell />
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminGananciasPage;
